import React, { FC, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Switch, Route } from 'react-router-dom';
import loadable from '@loadable/component';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { maxedPageWrapperStyle } from '@westwing/ui-kit/breakPoints';
import routes, { RouteInterface } from '../../common/routes';
import appleTouchIcon from '../assets/favicons/apple-touch-icon.png';
import favIcon16 from '../assets/favicons/favicon-16x16.png';
import favIcon32 from '../assets/favicons/favicon-32x32.png';
import favIconSafari from '../assets/favicons/safari-pinned-tab.svg';
import { RouterProvider } from '../hooks/useRouter';
import { UserTrackingProvider } from '../hooks/flags/useUserTracking';
import { addNotification } from '../redux/notifications/actions';
import { DisplayType } from '../redux/notifications/types';
import { cookiesEnabled } from '../../common/helpers/environment';
import useTranslator from '../hooks/useTranslator';
import WestwingEventHandler from '../WestwingEventHandler';
import useConfig from '../hooks/useConfig';
import fontStyles from './GlobalStyles/Fonts';
import Normalizer from './GlobalStyles/Normalizer';
import ClubIntegrationPixel from './ThirdPartyIntegration/ClubIntegrationPixel';
import theme from './GlobalStyles/theme';
import Overlays from './Overlays';
import ErrorBoundary from './Generic/ErrorBoundaries';
import NotFound from './NotFound';
import Notifications from './Notifications';
import RouteScrollToTop from './RouteScrollToTop';

interface AppProps {
    host?: string;
}

const StyledContentWrapper = styled.div`
    min-height: 100vh;
    position: relative;
    ${maxedPageWrapperStyle}
`;

const OneHeader = loadable(() => import('../../oneHeader/local/index'));
const ClubBarBanner = loadable(() => import('./ClubBarBanner'));
const OneFooter = loadable(() => import('./OneFooter'));

const App: FC<AppProps> = ({ host = '' }) => {
    const dispatch = useDispatch();
    const t = useTranslator();

    const { env } = useConfig();
    let { staticHost } = useConfig();
    if (env === 'local') {
        staticHost = '/';
    }

    const westwingEventHandler = WestwingEventHandler.toString();
    useEffect(() => {
        if (!cookiesEnabled) {
            const enableCookiesWarning = t(
                'In your browser, the cookies are not activated, which means that you can not use our site seamlessly. Please activate the cookies in the settings of your browser to use our website.'
            );
            dispatch(addNotification(enableCookiesWarning, 'sticky', DisplayType.warning));
        }
    }, [dispatch, t]);
    return (
        <ErrorBoundary boundaryName="App" fallbackComponent={<NotFound />}>
            <div className="pageContainer">
                <>
                    <Helmet style={[{ cssText: fontStyles(staticHost) }]}>
                        <title>WestwingNow</title>
                        <link rel="apple-touch-icon" sizes="180x180" href={appleTouchIcon} />
                        <link rel="icon" type="image/png" sizes="32x32" href={favIcon32} />
                        <link rel="icon" type="image/png" sizes="16x16" href={favIcon16} />
                        <link rel="mask-icon" href={favIconSafari} color={theme.colors.turquoise100} />
                        <link rel="manifest" href="/manifest.json" />
                        <script type="text/javascript">{`(${westwingEventHandler})().initWindow()`}</script>
                    </Helmet>
                    <Normalizer />
                    <Route>
                        {routerProps => (
                            <RouterProvider value={{ ...routerProps, host }}>
                                <RouteScrollToTop />
                                <UserTrackingProvider>
                                    <div className="page">
                                        <ErrorBoundary boundaryName="Header" fallbackComponent={null}>
                                            <OneHeader detachedHeaderMode={false} />
                                            <ClubBarBanner />
                                            <Notifications />
                                        </ErrorBoundary>
                                        <StyledContentWrapper>
                                            <Switch>
                                                {routes.map((route: RouteInterface) => (
                                                    <Route
                                                        key={route.path}
                                                        exact={route.exact}
                                                        path={route.path}
                                                        render={props => {
                                                            if (props.staticContext) {
                                                                // @ts-ignore
                                                                // eslint-disable-next-line no-param-reassign
                                                                props.staticContext.status = route.code;
                                                            }
                                                            return <route.component {...props} />;
                                                        }}
                                                    />
                                                ))}
                                            </Switch>
                                        </StyledContentWrapper>
                                        <ErrorBoundary boundaryName="Footer" fallbackComponent={null}>
                                            <OneFooter />
                                        </ErrorBoundary>
                                        <ClubIntegrationPixel />
                                    </div>
                                    <Overlays />
                                </UserTrackingProvider>
                            </RouterProvider>
                        )}
                    </Route>
                </>
            </div>
        </ErrorBoundary>
    );
};

export function InitSSR({ host = '' }: { host?: string }) {
    return <App host={host} />;
}

export default App;
